import * as React from 'react';

export const defaultMediaTokens = {
  motion: '(prefers-reduced-motion)',
  xs: '(min-width: 320px)',
  sm: '(min-width: 375px)',
  md: '(min-width: 425px)',
  lg: '(min-width: 768px)',
  xl: '(min-width: 1024px)',
  '2xl': '(min-width: 1440px)',
} as const;

export const abiMediaTokens = {
  mobileSm: '(min-width: 328px)',
  mobileMd: '(min-width: 430px)',
  tabletSm: '(min-width: 680px)',
  tabletMd: '(min-width: 960px)',
  desktopSm: '(min-width: 1230px)',
  desktopMd: '(min-width: 1304px)',
  desktopLg: '(min-width: 1592px)',
  desktopXl: '(min-width: 1728px)',
  desktopXl2: '(min-width: 1728px)',
} as const;

type Breakpoint =
  | keyof typeof defaultMediaTokens
  | keyof typeof abiMediaTokens
  | 'initial';

type ResponsiveBreakpointProps = {
  useOnlyAbiTokens?: boolean;
};

const extractBreakpoints = (mediaTokens: Record<string, string>) =>
  Object.entries(mediaTokens)
    .filter(([, value]) => value.includes('min-width'))
    .map(([key, value]) => [key, Number(value.match(/\d+/g)?.[0])])
    .sort((a, b) => Number(b[1]) - Number(a[1]));

const defaultBreakpoints = extractBreakpoints(defaultMediaTokens);
const abiBreakpoints = extractBreakpoints(abiMediaTokens);

function handleWindowResize(width: number, useOnlyAbiTokens = false) {
  const breakpoints = useOnlyAbiTokens ? abiBreakpoints : defaultBreakpoints;

  const breakpoint = breakpoints.find(
    ([, value]) => typeof value === 'number' && width >= value
  )?.[0];

  return (breakpoint as Breakpoint) ?? 'initial';
}

function subscribe(onStoreChange: () => void) {
  window?.addEventListener('resize', onStoreChange);
  return () => window?.removeEventListener('resize', onStoreChange);
}

export function useResponsiveBreakpoint({
  useOnlyAbiTokens,
}: ResponsiveBreakpointProps) {
  return React.useSyncExternalStore(
    subscribe,
    () => handleWindowResize(window?.innerWidth, useOnlyAbiTokens),
    () => undefined
  );
}
